import React, { FC } from 'react';
import usePhoneNumberInputStore from 'src/hooks/usePhoneNumberInputStore';
import Input from '../Input/Input';

interface Props {
  onChangeCallback?: (num: string) => void;
  onBlur?: () => void;
  value?: string;
  isWrong?: boolean;
  inputWrapperClassName?: string;
  required?: boolean;
  name?: string;
  disabled?: boolean;
  isBookingPage?: boolean;
}

const NUMBER_PREFIX = '+380';

export const isPhoneNumberValid = (phone: string) => {
  return /^\+?3?8?(0[\s.-]\d{2}[\s.-]\d{3}[\s.-]\d{2}[\s.-]\d{2})$/.test(phone);
};

export const isPhoneCodeValid = (phone: string) => {
  return /^\+?3?8?(0[\s.-](39|50|63|66|67|68|73|89|91|92|93|94|95|96|97|98|99)[\s.-]\d{3}[\s.-]\d{2}[\s.-]\d{2})$/.test(
    phone
  );
};

export const getMaskedPhoneValue = async (
  value: string,
  onChangeCallback: (phone: string) => Promise<void>,
  eventType?
) => {
  let updatedValue = value;

  if (!/^\d+$/.test(value[value?.length - 1])) {
    updatedValue = value.substring(0, value?.length - 1);
  }

  updatedValue = updatedValue.replace(/ /gm, '');
  updatedValue = updatedValue.replace(/^(380)/, '');
  updatedValue = updatedValue.replace(/^(\+380)/, '');
  updatedValue = updatedValue.replace(/\+/, '');
  updatedValue = updatedValue.replace('(', '').replace(')', '');

  // User tried to enter prefilled operator code. Remove it
  if (updatedValue === '0') {
    updatedValue = updatedValue.replace(/^(?:0)/, '');
  }

  if (eventType === 'deleteByCut') {
    await onChangeCallback('');
  } else if (eventType === 'deleteContentBackward') {
    // Do nothing if it's "delete backward" event and no number is entered
    if (value !== NUMBER_PREFIX) {
      await onChangeCallback(value);
    }
  } else {
    // this is state slow updating override
    let num = `${NUMBER_PREFIX} ( ${updatedValue.substring(
      0,
      2
    )} ) ${updatedValue.substring(2, 5)} ${updatedValue.substring(
      5,
      7
    )} ${updatedValue.substring(7, 9)}`;

    num = num.trim();

    // Removing extra space in brackets (_nn_)
    const part1 = num.substring(0, 6);
    const part2 = num.substring(7, 9);
    const part3 = num.substring(10);

    num = part1.concat(part2, part3);

    await onChangeCallback(num);
  }
};

const PhoneNumberInput: FC<Props> = ({
  required,
  onChangeCallback,
  value: externalValue,
  onBlur,
  isWrong,
  inputWrapperClassName,
  name,
  disabled,
  isBookingPage = false
}) => {
  const phoneNumber = usePhoneNumberInputStore((state) => state.phoneNumber);
  const setPhoneNumber = usePhoneNumberInputStore(
    (state) => state.setPhoneNumber
  );
  const phoneValue = isBookingPage ? phoneNumber : externalValue;

  const onChangeCallbackWrapper = async (newValue) => {
    const delayedUpdate = () =>
      new Promise((resolve) => {
        setTimeout(() => {
          resolve(
            isBookingPage
              ? setPhoneNumber(newValue)
              : onChangeCallback(newValue)
          );
        });
      });
    await delayedUpdate();
  };

  const handlePhoneNumberChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { value } = event.currentTarget;
    // eslint-disable-next-line
    // @ts-ignore
    const eventType = event.nativeEvent?.inputType || '';

    getMaskedPhoneValue(value, onChangeCallbackWrapper, eventType);
  };

  const handlePhoneNumberFocus = async () => {
    if (!phoneValue) {
      await onChangeCallbackWrapper(NUMBER_PREFIX);
    }
  };

  return (
    <Input
      data-cy="phone_number_input"
      leftPlaceComponent={
        <div className="text-[15px] text-black opacity-90 ml-2 pr-3 border-r border-alto">
          +380
        </div>
      }
      inputWrapperClassName={inputWrapperClassName}
      type="text"
      placeholder="(_ _)  _ _ _ ‒ _ _ ‒ _ _"
      onChange={handlePhoneNumberChange}
      onFocus={handlePhoneNumberFocus}
      value={phoneValue?.replace('+380', '')?.trim() || ''}
      onBlur={onBlur}
      isWrong={isWrong}
      required={required}
      name={name}
      disabled={disabled}
    />
  );
};

export default PhoneNumberInput;
